import React, { useState, useEffect, useCallback } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, CreditCard, DollarSign, Calendar, Send, CheckCircle, AlertCircle, Clock, ExternalLink } from "lucide-react";
import { usePermissions } from "../contexts/PermissionsContext";

const PaymentsPage = () => {
  const { can } = usePermissions();
  const [searchParams] = useSearchParams();
  const [payments, setPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState("all");
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const [form, setForm] = useState({
    project_id: "",
    payment_type: "deposit",
    amount: "",
    due_date: "",
    description: "",
    status: "draft",
    method: "card",
  });

  const paymentTypes = [
    { value: "deposit", label: "Deposit" },
    { value: "draw_1", label: "Draw 1" },
    { value: "draw_2", label: "Draw 2" },
    { value: "draw_3", label: "Draw 3" },
    { value: "draw_4", label: "Draw 4" },
    { value: "draw_5", label: "Draw 5" },
    { value: "final", label: "Final Payment" },
  ];

  const fetchData = async () => {
    try {
      const [paymentsRes, projectsRes] = await Promise.all([
        API.get("/payments"),
        API.get("/projects"),
      ]);
      setPayments(paymentsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  // Poll for payment status if session_id in URL
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.info("Payment status check timed out. Please refresh to check status.");
      return;
    }

    try {
      const response = await API.get(`/payments/checkout/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        toast.success("Payment successful!");
        fetchData();
        return;
      } else if (response.data.status === "expired") {
        toast.error("Payment session expired");
        return;
      }
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error("Error checking payment status:", error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      toast.info("Checking payment status...");
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, pollPaymentStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.post("/payments", {
        ...form,
        amount: parseFloat(form.amount) || 0,
      });
      toast.success("Payment request created");
      setDialogOpen(false);
      setForm({
        project_id: "",
        payment_type: "deposit",
        amount: "",
        due_date: "",
        description: "",
        status: "draft",
        method: "card",
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to create payment request");
    }
  };

  const handleSendPaymentRequest = async (payment) => {
    setCheckoutLoading(payment.payment_id);
    try {
      const response = await API.post(`/payments/${payment.payment_id}/checkout`, {
        origin_url: window.location.origin,
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Failed to create payment link");
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleUpdateStatus = async (paymentId, status) => {
    try {
      await API.put(`/payments/${paymentId}`, { status });
      toast.success("Payment status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update payment status");
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find((p) => p.project_id === projectId);
    return project?.name || "Unknown";
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: { bg: "bg-slate-800", text: "text-slate-600", icon: Clock },
      sent: { bg: "bg-blue-500/20", text: "text-blue-400", icon: Send },
      paid: { bg: "bg-emerald-500/20", text: "text-emerald-400", icon: CheckCircle },
      overdue: { bg: "bg-red-100", text: "text-red-700", icon: AlertCircle },
    };
    const style = styles[status] || styles.draft;
    const Icon = style.icon;
    return (
      <span className={`inline-flex items-center gap-1 status-badge ${style.bg} ${style.text}`}>
        <Icon size={12} />
        {status}
      </span>
    );
  };

  const filteredPayments = selectedProject && selectedProject !== "all"
    ? payments.filter((p) => p.project_id === selectedProject)
    : payments;

  const totalPending = filteredPayments
    .filter((p) => p.status !== "paid")
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  const totalPaid = filteredPayments
    .filter((p) => p.status === "paid")
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  if (loading) {
    return <div className="animate-pulse">Loading payments...</div>;
  }

  return (
    <div data-testid="payments-page" className="space-y-6 animate-slide-in">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-chivo font-bold text-white tracking-tight">Payments</h1>
          <p className="text-slate-400 font-ibm mt-1">Manage deposits and draw requests</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-48" data-testid="filter-project-select">
              <SelectValue placeholder="All Projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map((project) => (
                <SelectItem key={project.project_id} value={project.project_id}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {can('create_payment_request') && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-payment-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold text-white">
                  <Plus size={18} className="mr-2" />
                  New Payment Request
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-chivo">New Payment Request</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-400">Project *</label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                    <SelectTrigger data-testid="payment-project-select">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.project_id} value={project.project_id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-400">Payment Type</label>
                    <Select value={form.payment_type} onValueChange={(v) => setForm({ ...form, payment_type: v })}>
                      <SelectTrigger data-testid="payment-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-400">Amount ($) *</label>
                    <Input
                      type="number"
                      step="0.01"
                      data-testid="payment-amount-input"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      required
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-400">Due Date</label>
                    <Input
                      type="date"
                      data-testid="payment-due-date"
                      value={form.due_date}
                      onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-400">Payment Method</label>
                    <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                      <SelectTrigger data-testid="payment-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="card">Credit Card</SelectItem>
                        <SelectItem value="ach">ACH Bank Transfer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-400">Description</label>
                  <textarea
                    data-testid="payment-description-input"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-700 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary"
                    rows={2}
                    placeholder="Payment description..."
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="save-payment-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
                    Create Request
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <div className="flex items-center gap-2 text-slate-400 mb-2">
            <DollarSign size={18} />
            <span className="text-sm font-medium">Total Pending</span>
          </div>
          <p className="text-3xl font-chivo font-bold text-amber-600">
            ${totalPending.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <div className="flex items-center gap-2 text-slate-400 mb-2">
            <CheckCircle size={18} />
            <span className="text-sm font-medium">Total Received</span>
          </div>
          <p className="text-3xl font-chivo font-bold text-emerald-600">
            ${totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <div className="flex items-center gap-2 text-slate-400 mb-2">
            <CreditCard size={18} />
            <span className="text-sm font-medium">Payment Requests</span>
          </div>
          <p className="text-3xl font-chivo font-bold text-white">{filteredPayments.length}</p>
        </div>
      </div>

      {/* Payments List */}
      {filteredPayments.length === 0 ? (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-12 text-center">
          <CreditCard size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No payment requests yet</h3>
          <p className="text-slate-400 mb-4">Create your first payment request to collect deposits and draws</p>
        </div>
      ) : (
        <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800 border-b">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Project</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Type</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Amount</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Due Date</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Method</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredPayments.map((payment) => (
                <tr key={payment.payment_id} data-testid={`payment-row-${payment.payment_id}`}>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-white">{getProjectName(payment.project_id)}</p>
                      {payment.description && (
                        <p className="text-sm text-slate-400 truncate max-w-xs">{payment.description}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {paymentTypes.find((t) => t.value === payment.payment_type)?.label || payment.payment_type}
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-white">
                    ${payment.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="text-slate-400" />
                      {payment.due_date || "-"}
                    </div>
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(payment.status)}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {payment.method === "card" ? "Credit Card" : "ACH"}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {payment.status === "draft" && (
                        <Button
                          size="sm"
                          data-testid={`send-payment-${payment.payment_id}`}
                          onClick={() => handleSendPaymentRequest(payment)}
                          disabled={checkoutLoading === payment.payment_id}
                          className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                        >
                          {checkoutLoading === payment.payment_id ? (
                            "Loading..."
                          ) : (
                            <>
                              <Send size={14} className="mr-1" />
                              Send
                            </>
                          )}
                        </Button>
                      )}
                      {payment.status === "sent" && payment.stripe_checkout_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(payment.stripe_checkout_url, "_blank")}
                        >
                          <ExternalLink size={14} className="mr-1" />
                          Pay Link
                        </Button>
                      )}
                      {payment.status !== "paid" && (
                        <Select
                          value={payment.status}
                          onValueChange={(v) => handleUpdateStatus(payment.payment_id, v)}
                        >
                          <SelectTrigger className="w-28 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="sent">Sent</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="overdue">Overdue</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PaymentsPage;
